import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Paperclip, Upload, Trash2, Download, FileText, Image as ImageIcon, File as FileIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from './ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function iconFor(contentType) {
  if (!contentType) return <FileIcon className="w-4 h-4" />;
  if (contentType.startsWith('image/')) return <ImageIcon className="w-4 h-4 text-blue-500" />;
  if (contentType.includes('pdf')) return <FileText className="w-4 h-4 text-red-500" />;
  return <FileIcon className="w-4 h-4 text-slate-500" />;
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function Attachments({ entityType, entityId, canUpload = true, canDelete = true, currentUser = null, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!entityType || !entityId) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${BACKEND_URL}/api/attachments/${entityType}/${entityId}`,
        { withCredentials: true }
      );
      setItems(res.data || []);
    } catch (err) {
      // 404 cuando aún no existe la entidad: silencioso
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`"${f.name}" supera los 10MB`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        await axios.post(
          `${BACKEND_URL}/api/attachments/${entityType}/${entityId}`,
          fd,
          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      toast.success('Adjuntos subidos');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir adjunto');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleView = async (att) => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/attachments/file/${att.attachment_id}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error('No se pudo abrir el adjunto');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/attachments/file/${confirmDelete.attachment_id}`,
        { withCredentials: true }
      );
      toast.success('Adjunto eliminado');
      setConfirmDelete(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const canRemove = (att) => {
    if (!canDelete) return false;
    if (!currentUser) return true;
    if (currentUser.role === 'admin' || currentUser.role === 'supervisor') return true;
    return att.uploaded_by === currentUser.user_id;
  };

  return (
    <div className={compact ? '' : 'space-y-3'} data-testid={`attachments-${entityType}`}>
      {!compact && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Paperclip className="w-4 h-4" />
            <span className="text-sm font-medium">Adjuntos ({items.length})</span>
          </div>
          {canUpload && (
            <label className="cursor-pointer">
              <input
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.xls,.xlsx,.csv,.doc,.docx,.txt"
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
                data-testid={`attach-input-${entityType}`}
              />
              <span className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md hover:bg-accent">
                <Upload className="w-4 h-4" /> {uploading ? 'Subiendo...' : 'Adjuntar'}
              </span>
            </label>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando adjuntos...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">Sin adjuntos.</p>
      ) : (
        <div className="space-y-1">
          {items.map((att) => (
            <div
              key={att.attachment_id}
              className="flex items-center justify-between gap-2 px-3 py-2 border rounded-md bg-background hover:bg-accent/40"
            >
              <button
                type="button"
                onClick={() => handleView(att)}
                className="flex items-center gap-2 text-sm text-left flex-1 min-w-0"
                data-testid={`attach-view-${att.attachment_id}`}
              >
                {iconFor(att.content_type)}
                <span className="truncate">{att.original_filename}</span>
                <span className="text-xs text-muted-foreground shrink-0">{formatSize(att.size)}</span>
              </button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleView(att)}
                title="Ver/Descargar"
              >
                <Download className="w-4 h-4" />
              </Button>
              {canRemove(att) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setConfirmDelete(att)}
                  title="Eliminar"
                  data-testid={`attach-delete-${att.attachment_id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title="Eliminar adjunto"
        description={<>¿Eliminar el adjunto <strong>{confirmDelete?.original_filename}</strong>?</>}
        loading={deleting}
        onConfirm={handleDelete}
        testId="confirm-delete-attachment"
      />
    </div>
  );
}

export default Attachments;
